import{Component}from '@theme/component';import{fetchConfig,debounce,onAnimationEnd,prefersReducedMotion,resetShimmer,startViewTransition,}from '@theme/utilities';import{morphSection,sectionRenderer}from '@theme/section-renderer';import{ThemeEvents,CartUpdateEvent,QuantitySelectorUpdateEvent,CartAddEvent,DiscountUpdateEvent,}from '@theme/events';import{cartPerformance}from '@theme/performance';
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 100).bind(this);
  #pendingQuantityUpdates = new Map();
  #quantityUpdateInFlight = false;
  #sectionRefreshTimer = null;
  #sectionRefreshToken = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#queueQuantityUpdate({ line, quantity, action: 'change' });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;
    const textComponent = lineItemRow.querySelector('text-component');
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;
    const lineItemKey = cartItemRowToRemove?.dataset.key;

    this.#pendingQuantityUpdates.delete(line);
    this.updateQuantity({ line, id: lineItemKey, quantity: 0, action: 'clear' });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const template = this.hasAttribute('data-drawer')
      ? this.closest('cart-drawer-component')?.querySelector('#empty-cart-drawer-template')
      : this.querySelector('#empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  #queueQuantityUpdate(config) {
    this.#sectionRefreshToken += 1;
    if (this.#sectionRefreshTimer) window.clearTimeout(this.#sectionRefreshTimer);
    this.#pendingQuantityUpdates.set(config.line, config);
    this.#flushQuantityUpdates();
  }

  #flushQuantityUpdates() {
    if (this.#quantityUpdateInFlight) return;

    const nextUpdate = this.#pendingQuantityUpdates.values().next().value;
    if (!nextUpdate) return;

    this.#pendingQuantityUpdates.delete(nextUpdate.line);
    this.#quantityUpdateInFlight = true;

    this.updateQuantity(nextUpdate).finally(() => {
      this.#quantityUpdateInFlight = false;
      if (this.#pendingQuantityUpdates.size) {
        this.#flushQuantityUpdates();
      } else {
        this.#scheduleQuantitySectionRefresh();
      }
    });
  }

  #hasQueuedQuantityUpdate(config) {
    return config.action === 'change' && this.#pendingQuantityUpdates.size > 0;
  }

  #scheduleQuantitySectionRefresh() {
    if (this.#sectionRefreshTimer) window.clearTimeout(this.#sectionRefreshTimer);

    const token = ++this.#sectionRefreshToken;
    this.#sectionRefreshTimer = window.setTimeout(async () => {
      if (token !== this.#sectionRefreshToken || this.#quantityUpdateInFlight || this.#pendingQuantityUpdates.size) return;

      try {
        const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false);
        if (token !== this.#sectionRefreshToken || this.#quantityUpdateInFlight || this.#pendingQuantityUpdates.size) return;

        morphSection(this.sectionId, sectionHTML, this.isDrawer ? 'hydration' : 'full');
        this.#updateCartQuantitySelectorButtonStates();
      } catch (error) {
        console.error(error);
      }
    }, 120);
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    if (config.action !== 'change') this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const requestBody = {
      quantity,
      sections_url: window.location.pathname,
    };

    if (config.id) {
      requestBody.id = config.id;
    } else {
      requestBody.line = line;
    }

    const body = JSON.stringify(requestBody);

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (this.#hasQueuedQuantityUpdate(config)) {
          return;
        }

        if (!parsedResponseText.sections?.[this.sectionId]) {
          this.#updateQuantitySelectors(parsedResponseText);
          this.dispatchEvent(
            new CartUpdateEvent(parsedResponseText, this.sectionId, {
              itemCount: Number(parsedResponseText.item_count || 0),
              source: 'cart-items-component',
              cart: parsedResponseText,
            })
          );
          if (config.action !== 'change') this.#scheduleQuantitySectionRefresh();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);
        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (config.action !== 'change') this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      selector.updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
